import { Type } from '@sinclair/typebox';
import { createPathGuidParameter } from '../../routes/createPathParameter';

const aiConfigResponseSchema = Type.Object({
    chatModel: Type.String(),
    embeddingModel: Type.String(),
    bedrockRegion: Type.String(),
    isConfigured: Type.Boolean(),
});

// Conversation-list and history endpoints. Both are read-only — they back
// the chat side panel's history view. Posting new messages is handled by
// the SSE-streaming endpoints registered elsewhere (see week 6 controller),
// not in the OpenAPI surface, since SSE responses don't fit OpenAPI's
// response-shape model.
const chatConversationSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    title: Type.Union([Type.String(), Type.Null()]),
    contextType: Type.Union([Type.Literal('general'), Type.Literal('object'), Type.Literal('survey')]),
    contextId: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    created_at: Type.Union([Type.String(), Type.Null()]),
    updated_at: Type.Union([Type.String(), Type.Null()]),
});

const chatMessageSourceSchema = Type.Object({
    type: Type.Union([Type.Literal('object'), Type.Literal('survey'), Type.Literal('asset')]),
    id: Type.String({ format: 'uuid' }),
    displayName: Type.String(),
    url: Type.String(),
});

const chatMessageProgressEventSchema = Type.Object({
    kind: Type.Union([Type.Literal('rotate'), Type.Literal('tool')]),
    text: Type.String(),
    tool: Type.Union([Type.String(), Type.Null()]),
    at: Type.String(),
});

const chatMessageSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    role: Type.String(),
    content: Type.String(),
    tokensIn: Type.Number(),
    tokensOut: Type.Number(),
    model: Type.Union([Type.String(), Type.Null()]),
    interrupted: Type.Boolean(),
    sources: Type.Array(chatMessageSourceSchema),
    progressEvents: Type.Array(chatMessageProgressEventSchema),
    created_at: Type.Union([Type.String(), Type.Null()]),
});

export const aiPaths = {
    '/api/ai/config': {
        get: {
            operationId: 'aiChatControllers.getAiConfig',
            responses: {
                '200': {
                    description: 'AI configuration',
                    content: {
                        'application/json': {
                            schema: aiConfigResponseSchema,
                        },
                    },
                },
            },
        },
    },
    '/api/ai/chat/conversations': {
        get: {
            operationId: 'aiChatControllers.getConversations',
            responses: {
                '200': {
                    description: 'List of conversations',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(chatConversationSchema),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/ai/chat/conversations/{conversationId}/messages': {
        parameters: [createPathGuidParameter('conversationId')],
        get: {
            operationId: 'aiChatControllers.getMessages',
            responses: {
                '200': {
                    description: 'Messages in conversation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                items: Type.Array(chatMessageSchema),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/ai/chat/contexts/{contextType}/{contextId}/conversation': {
        parameters: [
            {
                in: 'path',
                name: 'contextType',
                required: true,
                schema: Type.Union([Type.Literal('object'), Type.Literal('survey')]),
            },
            createPathGuidParameter('contextId'),
        ],
        get: {
            operationId: 'aiChatControllers.getConversationForContext',
            responses: {
                '200': {
                    description: 'Existing conversation for this user+context, or null if none exists yet',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                conversation: Type.Union([Type.Object({ id: Type.String({ format: 'uuid' }) }), Type.Null()]),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/ai/chat/general/conversation': {
        get: {
            operationId: 'aiChatControllers.getGeneralConversation',
            responses: {
                '200': {
                    description: 'Existing general (dashboard) conversation for this user, or null if none exists yet',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                conversation: Type.Union([Type.Object({ id: Type.String({ format: 'uuid' }) }), Type.Null()]),
                            }),
                        },
                    },
                },
            },
        },
    },
} as const;
